// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * play.ts — the live game surface: triage → the stop → the stint reveal, looped
 * for every pit stop of the race.
 *
 * TRIAGE is the game (the idea's whole thesis). You read the telemetry and the
 * radio and choose which systems the crew services. THE STOP is the execution: a
 * short dependency-ordered tap sequence — jack up before the wheels come off,
 * wheels on before the jack drops, GO only when it is all clear. Fumbles cost
 * tenths; an unsafe release is a penalty, not a loss. The STINT reveal shows what
 * the calls cost on track, then the next car comes in.
 *
 * Board-game control scheme (MOBILE_CONTROLS §2 spirit): tap the right thing at
 * the right time. No D-pad, no joystick — those are for continuous steering, and
 * there is none here. Every hit target is ≥44px.
 */

import type { Sfx } from '@ben-gy/game-engine/sound';
import { el, clear } from './dom';
import {
  JOB_TIME,
  BASE_STOP,
  CATA_COST,
  SYSTEM_NAME,
  type Game,
  type StintOutcome,
  type Summary,
  type System,
} from './game';
import type { Mode } from './modes';
import type { Standing } from './race';
import { startCountdown, type Countdown } from './countdown';

const FUMBLE_SEC = 0.5;
const UNSAFE_SEC = 2.0;
const STINT_REVEAL_MS = 2600;

export interface PlayConfig {
  game: Game;
  mode: Mode;
  multiplayer: boolean;
  sfx: Sfx;
  /** After each committed stop, for the race status ping. */
  onProgress: (stopIndex: number, total: number) => void;
  onFinish: (summary: Summary) => void;
  onMenu: () => void;
}

export interface PlayView {
  root: HTMLElement;
  setStandings(list: Standing[]): void;
  destroy(): void;
}

type Band = { key: 'ok' | 'watch' | 'crit'; label: string };

function band(sev: number): Band {
  if (sev < 0.45) return { key: 'ok', label: 'OK' };
  if (sev < 0.75) return { key: 'watch', label: 'WATCH' };
  return { key: 'crit', label: 'CRITICAL' };
}

function fmt(sec: number): string {
  return `${sec.toFixed(1)}s`;
}

export function createPlay(cfg: PlayConfig): PlayView {
  const g = cfg.game;
  let destroyed = false;
  const timers = new Set<ReturnType<typeof setTimeout>>();
  let countdown: Countdown | null = null;
  let standings: Standing[] = [];

  const root = el('div', { class: 'play' });

  // ── HUD ─────────────────────────────────────────────────────────────────
  const hud = el('div', { class: 'hud' });
  const hudMode = el('span', { class: 'hud-mode' });
  const hudStop = el('span', { class: 'hud-stop' });
  const hudTotal = el('span', { class: 'hud-total' });
  const menuBtn = el('button', { class: 'hud-menu', 'aria-label': 'Menu', type: 'button' }, ['☰']);
  menuBtn.addEventListener('click', () => cfg.onMenu());
  hud.append(hudMode, hudStop, hudTotal, menuBtn);
  root.append(hud);

  const strip = el('div', { class: 'standings', hidden: !cfg.multiplayer });
  root.append(strip);

  const stage = el('div', { class: 'stage' });
  root.append(stage);

  const toastEl = el('div', { class: 'toast', role: 'status', 'aria-live': 'polite', hidden: true });
  root.append(toastEl);

  function later(fn: () => void, ms: number): void {
    const t = setTimeout(() => {
      timers.delete(t);
      if (!destroyed) fn();
    }, ms);
    timers.add(t);
  }

  function toast(msg: string, kind: 'good' | 'bad' | '' = ''): void {
    toastEl.textContent = msg;
    toastEl.className = `toast ${kind}`;
    toastEl.hidden = false;
    later(() => {
      toastEl.hidden = true;
    }, 2200);
  }

  function paintHud(): void {
    hudMode.textContent = cfg.mode.name;
    hudStop.textContent = g.over ? 'FINISHED' : `STOP ${g.stopIndex + 1} / ${g.stops}`;
    hudTotal.textContent = fmt(g.liveTotal());
  }

  function paintStandings(): void {
    if (!cfg.multiplayer) return;
    clear(strip);
    for (const s of standings) {
      const chip = el('div', { class: `s-chip${s.isSelf ? ' me' : ''}${s.gone ? ' gone' : ''}` });
      chip.append(el('span', { class: 's-name', text: s.name }));
      chip.append(
        el('span', {
          class: 's-prog',
          text: s.done ? fmt(s.total) : `${Math.min(s.stop, s.stops || g.stops)}/${g.stops}`,
        }),
      );
      strip.append(chip);
    }
  }

  // ── phase: triage ─────────────────────────────────────────────────────────
  const fix = new Set<System>();

  function renderTriage(): void {
    fix.clear();
    paintHud();
    clear(stage);
    const view = g.view();

    const panel = el('div', { class: 'triage' });

    // radio
    const r = view.radio;
    const radio = el('div', { class: `radio${r.alarm ? ' alarm' : ''}` });
    radio.append(el('span', { class: 'radio-tag', text: 'RADIO' }));
    radio.append(el('span', { class: 'radio-text', text: `“${r.text}”` }));
    panel.append(radio);
    cfg.sfx.play(r.alarm ? 'hit' : 'blip');

    const help = el('p', {
      class: 'triage-help',
      text: 'Tap a system to service it. Every job costs seconds in the stop; every fault you leave costs seconds on track — and a red one blows up.',
    });
    panel.append(help);

    const rows = el('div', { class: 'tele' });
    const rowEls: Array<() => void> = [];
    for (const t of view.telemetry) {
      const s = t.system;
      const offline = t.reading === null;
      const b = offline ? { key: 'ok' as const, label: 'NO DATA' } : band(t.reading!);
      const flagged = r.alarm && r.system === s;

      const row = el('button', {
        class: 'tele-row',
        type: 'button',
        'aria-pressed': 'false',
        'data-band': offline ? 'offline' : b.key,
      });

      const head = el('div', { class: 'tele-head' });
      head.append(el('span', { class: 'tele-name', text: SYSTEM_NAME[s] }));
      if (flagged) head.append(el('span', { class: 'tele-flag', text: '📻 radio' }));
      head.append(el('span', { class: 'tele-state', text: offline ? 'NO DATA' : b.label }));

      const barWrap = el('div', { class: 'bar' });
      const barFill = el('div', {
        class: `bar-fill${offline ? ' offline' : ''}`,
      });
      barFill.style.width = offline ? '100%' : `${Math.round(t.reading! * 100)}%`;
      barWrap.append(barFill);
      if (offline) barWrap.append(el('span', { class: 'bar-nodata', text: '?? sensor offline' }));

      const call = el('span', { class: 'tele-call', text: 'SKIP' });

      row.append(head, barWrap, call);

      const update = (): void => {
        const on = fix.has(s);
        row.classList.toggle('fix', on);
        row.setAttribute('aria-pressed', on ? 'true' : 'false');
        call.textContent = on ? `FIX · +${JOB_TIME[s].toFixed(1)}s` : 'SKIP';
      };
      row.addEventListener('click', () => {
        if (fix.has(s)) fix.delete(s);
        else fix.add(s);
        cfg.sfx.play('select');
        update();
      });
      update();
      rowEls.push(update);
      rows.append(row);
    }
    panel.append(rows);

    const actions = el('div', { class: 'triage-actions' });
    const clearBtn = el('button', { class: 'btn ghost', type: 'button' }, ['Skip all']);
    clearBtn.addEventListener('click', () => {
      fix.clear();
      rowEls.forEach((u) => u());
      cfg.sfx.play('blip');
    });
    const confirm = el('button', { class: 'btn primary big', type: 'button' }, [
      'Call the stop →',
    ]);
    confirm.addEventListener('click', () => {
      cfg.sfx.play('powerup');
      renderStop();
    });
    actions.append(clearBtn, confirm);
    panel.append(actions);

    stage.append(panel);
  }

  // ── phase: the stop (execution) ─────────────────────────────────────────────
  function renderStop(): void {
    clear(stage);
    const doTyres = fix.has('tyres');

    const state = {
      jackUp: false,
      corners: { FL: false, FR: false, RL: false, RR: false } as Record<string, boolean>,
      jackDown: false,
      fuel: !fix.has('fuel'),
      wing: !fix.has('wing'),
      brakes: !fix.has('brakes'),
      clock: BASE_STOP,
      penalties: 0,
      fumbles: 0,
      unsafe: 0,
      released: false,
    };

    const cornerShare = doTyres ? JOB_TIME.tyres / 6 : 0;

    const wrap = el('div', { class: 'stopwrap' });

    const clockEl = el('div', { class: 'stopclock' });
    const clockNum = el('span', { class: 'stopclock-num' });
    clockEl.append(el('span', { class: 'stopclock-tag', text: 'STOP' }), clockNum);
    wrap.append(clockEl);

    const grid = el('div', { class: 'stations' });
    wrap.append(grid);

    const goBtn = el('button', { class: 'btn go', type: 'button' }, ['GO']);
    wrap.append(goBtn);

    const stations: HTMLButtonElement[] = [];

    const allCorners = (): boolean =>
      state.corners.FL && state.corners.FR && state.corners.RL && state.corners.RR;
    const goReady = (): boolean =>
      (!doTyres || state.jackDown) && state.fuel && state.wing && state.brakes;

    function paintClock(): void {
      clockNum.textContent = fmt(state.clock + state.penalties);
    }

    function bump(share: number): void {
      state.clock += share;
      paintClock();
    }

    function fumble(btn: HTMLButtonElement): void {
      state.fumbles++;
      state.penalties += FUMBLE_SEC;
      cfg.sfx.play('hit');
      btn.classList.remove('shake');
      void btn.offsetWidth;
      btn.classList.add('shake');
      paintClock();
    }

    // jack
    let jackBtn: HTMLButtonElement | null = null;
    if (doTyres) {
      jackBtn = el('button', { class: 'station jack', type: 'button' }) as HTMLButtonElement;
      jackBtn.addEventListener('click', () => {
        if (!state.jackUp) {
          state.jackUp = true;
          cfg.sfx.play('select');
          bump(cornerShare);
          repaint();
        } else if (allCorners() && !state.jackDown) {
          state.jackDown = true;
          cfg.sfx.play('select');
          bump(cornerShare);
          repaint();
        } else {
          fumble(jackBtn!);
        }
      });
      grid.append(jackBtn);
      stations.push(jackBtn);

      for (const c of ['FL', 'FR', 'RL', 'RR'] as const) {
        const b = el('button', { class: 'station corner', type: 'button' }) as HTMLButtonElement;
        b.dataset.corner = c;
        b.addEventListener('click', () => {
          if (state.jackUp && !state.corners[c]) {
            state.corners[c] = true;
            cfg.sfx.play('coin');
            bump(cornerShare);
            repaint();
          } else {
            fumble(b);
          }
        });
        grid.append(b);
        stations.push(b);
      }
    }

    const simple: Array<{ key: 'fuel' | 'wing' | 'brakes'; label: string; sound: 'jump' | 'select' }> =
      [
        { key: 'fuel', label: 'FUEL', sound: 'jump' },
        { key: 'wing', label: 'WING', sound: 'select' },
        { key: 'brakes', label: 'BRAKES', sound: 'select' },
      ];
    for (const item of simple) {
      if (!fix.has(item.key)) continue;
      const b = el('button', { class: `station ${item.key}`, type: 'button' }) as HTMLButtonElement;
      b.addEventListener('click', () => {
        if (!state[item.key]) {
          state[item.key] = true;
          cfg.sfx.play(item.sound);
          bump(JOB_TIME[item.key]);
          repaint();
        } else {
          fumble(b);
        }
      });
      grid.append(b);
      stations.push(b);
    }

    goBtn.addEventListener('click', () => {
      if (state.released) return;
      if (!goReady()) {
        state.unsafe++;
        state.penalties += UNSAFE_SEC;
        cfg.sfx.play('explosion');
        root.classList.remove('shakeall');
        void root.offsetWidth;
        root.classList.add('shakeall');
        toast('UNSAFE RELEASE — the crew wasn’t clear. +2.0s', 'bad');
        paintClock();
        return;
      }
      state.released = true;
      cfg.sfx.play('powerup');
      const execPenalty = state.penalties;
      const outcome = g.commit(fix, execPenalty);
      cfg.onProgress(g.stopIndex, g.liveTotal());
      renderStint(outcome);
    });

    function repaint(): void {
      // jack
      if (jackBtn) {
        let label = 'JACK';
        let ready = false;
        let done = false;
        if (!state.jackUp) {
          label = 'JACK UP';
          ready = true;
        } else if (!allCorners()) {
          label = 'CAR UP';
          done = true;
        } else if (!state.jackDown) {
          label = 'JACK DOWN';
          ready = true;
        } else {
          label = 'DROPPED';
          done = true;
        }
        jackBtn.textContent = label;
        jackBtn.classList.toggle('ready', ready);
        jackBtn.classList.toggle('done', done && !ready);
      }
      for (const b of stations) {
        const c = b.dataset.corner;
        if (c) {
          const done = state.corners[c];
          b.textContent = done ? `${c} ✓` : c;
          b.classList.toggle('ready', state.jackUp && !done);
          b.classList.toggle('done', done);
        }
      }
      for (const item of simple) {
        if (!fix.has(item.key)) continue;
        const b = stations.find((x) => x.classList.contains(item.key));
        if (!b) continue;
        const done = state[item.key];
        b.textContent = done ? `${item.label} ✓` : item.label;
        b.classList.toggle('ready', !done);
        b.classList.toggle('done', done);
      }
      const ready = goReady();
      goBtn.classList.toggle('ready', ready);
      goBtn.textContent = ready ? 'GO' : 'HOLD';
    }

    paintClock();
    repaint();
    stage.append(wrap);
    paintHud();
  }

  // ── phase: the stint reveal ─────────────────────────────────────────────────
  function renderStint(outcome: StintOutcome): void {
    clear(stage);
    paintHud();

    const panel = el('div', { class: 'stint' });
    panel.append(el('div', { class: 'stint-tag', text: 'BACK ON TRACK' }));

    let anyCata = false;
    const list = el('div', { class: 'stint-list' });
    for (const so of outcome.systems) {
      const b = band(so.sevOut);
      const row = el('div', {
        class: `stint-row${so.catastrophe ? ' cata' : ''}`,
        'data-band': so.catastrophe ? 'crit' : b.key,
      });
      row.append(el('span', { class: 'stint-sys', text: SYSTEM_NAME[so.system] }));
      let note: string;
      if (so.fixed) note = 'serviced';
      else if (so.catastrophe) note = 'FAILED';
      else if (so.penalty < 0.1) note = 'nursed it';
      else note = `lost ${fmt(so.penalty)}`;
      row.append(el('span', { class: 'stint-note', text: note }));
      if (so.catastrophe) {
        anyCata = true;
        row.append(el('span', { class: 'stint-cost', text: `+${fmt(CATA_COST[so.system])}` }));
      }
      list.append(row);
    }
    panel.append(list);

    panel.append(
      el('div', { class: 'stint-total' }, [
        el('span', { text: `Stop + stint: ` }),
        el('strong', { text: `+${fmt(outcome.lost)}` }),
      ]),
    );

    if (anyCata) {
      cfg.sfx.play('lose');
      root.classList.remove('shakeall');
      void root.offsetWidth;
      root.classList.add('shakeall');
    } else {
      cfg.sfx.play('coin');
    }

    const next = el('button', { class: 'btn primary', type: 'button' }, [
      g.over ? 'See the result →' : 'Next car in →',
    ]);
    const advance = (): void => {
      if (g.over) cfg.onFinish(g.summary());
      else renderTriage();
    };
    next.addEventListener('click', advance);
    panel.append(next);

    stage.append(panel);
    // Auto-advance so a multiplayer race never stalls on one reader.
    later(advance, STINT_REVEAL_MS);
  }

  // ── boot: count everyone in, then the first car arrives ─────────────────────
  paintHud();
  paintStandings();
  countdown = startCountdown({
    container: root,
    sfx: cfg.sfx,
    onDone: () => {
      countdown = null;
      renderTriage();
    },
  });

  return {
    root,
    setStandings(list): void {
      standings = list;
      paintStandings();
    },
    destroy(): void {
      destroyed = true;
      countdown?.cancel();
      for (const t of timers) clearTimeout(t);
      timers.clear();
    },
  };
}
